import { Button, createFormField, Tooltip, useMinDelayState } from '@ibsys/kit';
import { CheckIcon, CircleX, CodeIcon, TriangleAlert, TypeIcon } from 'lucide-react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PredicateIssueSchema, useConvertPredicate } from '@/entities/predicate';
import { AiInput, AiTextarea } from '@/shared/ui';
import { cn } from '@/shared/lib';

type Value = {
  dsl: string;
  dslHuman: string;
};

type Props = {
  value?: Value;
  onChange?: (value: Value) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  classNames?: { input?: string };
  type?: 'textarea' | 'input';
  label?: string;
  name?: string;
  required?: boolean;
  /** Вызывается при ошибке с сервера (конвертация). error = null когда ошибка сброшена */
  onError?: (error: string | null) => void;
  error?: string;
};

const PredicateInputBase: FC<Props> = React.memo((props) => {
  const {
    className,
    value,
    onChange,
    placeholder,
    disabled,
    classNames,
    type = 'textarea',
    label,
    required,
    onError,
    error,
  } = props;

  const { t } = useTranslation('common');
  const { mutateAsync: convert, isPending } = useConvertPredicate();

  const [isHuman, setIsHuman] = useState(true);
  // Локальное состояние для отображения текста (не сбрасывается при ошибках)
  const [displayDsl, setDisplayDsl] = useState(value?.dsl || '');
  const [displayDslHuman, setDisplayDslHuman] = useState(value?.dslHuman || '');
  const [issues, setIssues] = useState<PredicateIssueSchema[] | null>(null);

  // Используем ref для отслеживания предыдущего value, чтобы определить изменения извне
  const prevValueRef = useRef<Value | undefined>(value);

  // Используем displayValue для отображения, но value для контроля
  const displayTerm = isHuman ? displayDslHuman : displayDsl;
  const isMagic = useMinDelayState(isPending, 600);

  // Синхронизируем displayValue с value только когда value изменился извне (не из-за ошибок)
  // Если есть issues, не синхронизируем - сохраняем текст в поле
  useEffect(() => {
    const prevValue = prevValueRef.current;
    const currentValue = value;

    // Определяем, изменилось ли value извне (не из-за нашей логики с ошибками)
    const valueChangedExternally =
      prevValue?.dsl !== currentValue?.dsl || prevValue?.dslHuman !== currentValue?.dslHuman;

    // Обновляем displayValue только если:
    // 1. value изменилось извне
    // 2. нет активных ошибок (issues === null)
    if (valueChangedExternally && issues === null) {
      if (currentValue?.dsl !== undefined) {
        setDisplayDsl(currentValue.dsl);
      }
      if (currentValue?.dslHuman !== undefined) {
        setDisplayDslHuman(currentValue.dslHuman);
      }
      // Если value стало пустым, очищаем displayValue
      if (!currentValue?.dsl && !currentValue?.dslHuman) {
        setDisplayDsl('');
        setDisplayDslHuman('');
      }
    }

    // Обновляем ref для следующего рендера
    prevValueRef.current = currentValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // displayDsl и displayDslHuman намеренно не включены в зависимости,
    // чтобы избежать бесконечных циклов. Мы используем prevValueRef для отслеживания изменений value.
  }, [value, issues]);

  const clear = () => {
    setDisplayDsl('');
    setDisplayDslHuman('');
    setIssues(null);
    onError?.(null);
    onChange?.({
      dsl: '',
      dslHuman: '',
    });
  };

  const sendRequest = async (text: string) => {
    const isChanged = (isHuman ? value?.dslHuman : value?.dsl) !== text;
    if (!isPending && isChanged) {
      const response = await convert({
        dsl: isHuman ? undefined : text,
        dslHuman: isHuman ? text : undefined,
        entityType: 'сonversation',
      });
      const issues = response?.issues?.length ? response.issues : null;
      setIssues(issues);
      if (!issues) {
        onError?.(null);
        if (response.dsl && response.dslHuman) {
          // Обновляем displayValue при успехе
          setDisplayDsl(response.dsl);
          setDisplayDslHuman(response.dslHuman);
          // Вызываем onChange с новыми значениями
          onChange?.({
            dsl: response.dsl,
            dslHuman: response.dslHuman,
          });
        }
      } else {
        const convertError = issues.find((i) => i.type === 'ConvertationError');
        const errorMessage = convertError?.message ?? null;
        onError?.(errorMessage);
        // При ошибках сбрасываем value в onChange, но сохраняем displayValue
        onChange?.({
          dsl: '',
          dslHuman: '',
        });
        // displayDsl и displayDslHuman остаются без изменений - текст не удаляется
      }
    }
  };

  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    if (!isMagic) {
      const newValue = event.target.value;
      // Обновляем только displayValue (визуальное отображение)
      if (isHuman) {
        setDisplayDslHuman(newValue);
      } else {
        setDisplayDsl(newValue);
      }
      setIssues(null);
      onError?.(null);
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> = () => {
    if (displayTerm) {
      sendRequest(displayTerm);
    } else {
      clear();
    }
  };

  const handleKeyUp: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    if (type === 'input' && event.code === 'Enter') {
      if (displayTerm) {
        sendRequest(displayTerm);
      } else {
        clear();
      }
    }
  };

  const toggleDsl = () => {
    const next = !isHuman;
    setIsHuman(next);
  };

  const getConversionStatus = () => {
    if (!issues || issues.length === 0) {
      return { type: 'success' as const, message: null };
    }

    const hasConvertationError = issues.some((issue) => issue.type === 'ConvertationError');
    if (hasConvertationError) {
      const errorIssue = issues.find((issue) => issue.type === 'ConvertationError');
      return { type: 'error' as const, message: errorIssue?.message || null };
    }

    const firstIssue = issues[0];
    return { type: 'warning' as const, message: firstIssue?.message || null };
  };

  const status = getConversionStatus();

  const renderStatusIcon = () => {
    // Don't show status if there's no conversion result yet
    if (issues === null) {
      return null;
    }

    if (status.type === 'success') {
      return (
        <div className="flex h-5 w-5 items-center justify-center rounded bg-green-500">
          <CheckIcon className="h-3 w-3 text-white" />
        </div>
      );
    }

    if (status.type === 'error') {
      const icon = (
        <div className="flex h-5 w-5 items-center justify-center rounded bg-red-500">
          <CircleX className="h-3 w-3 text-white" />
        </div>
      );

      if (status.message) {
        return (
          <Tooltip
            tooltip={
              <div className="text-sm text-red-600">
                <span>{status.message}</span>
              </div>
            }
            classNames={{ content: 'bg-red-100 border-red-700 textred-700 p-2' }}
          >
            {icon}
          </Tooltip>
        );
      }

      return icon;
    }

    if (status.type === 'warning') {
      const icon = (
        <div className="flex h-5 w-5 items-center justify-center rounded bg-yellow-500">
          <TriangleAlert className="h-3 w-3 text-white" />
        </div>
      );

      if (status.message) {
        return (
          <Tooltip
            tooltip={
              <div className="text-sm text-white">
                <span>{status.message}</span>
              </div>
            }
            classNames={{ content: 'bg-yellow-600 p-2' }}
          >
            {icon}
          </Tooltip>
        );
      }

      return icon;
    }

    return null;
  };

  const iconRight = (
    <div className={'flex gap-2 items-center'}>
      {renderStatusIcon()}
      <Button
        tabIndex={-1}
        size={'sm'}
        variant={'ghost'}
        disabled={isMagic}
        icon={isHuman ? <CodeIcon /> : <TypeIcon />}
        onClick={toggleDsl}
      />
    </div>
  );

  const commonProps = {
    iconRight,
    placeholder: placeholder || t('predicateInputPlaceholder'),
    value: displayTerm,
    label,
    required,
    className,
    disabled: disabled,
    classNames: {
      ...classNames,
      iconRight: cn(label ? 'top-10' : 'top-5'),
    },
    isProcessing: isMagic,
    onPressUp: handleKeyUp,
    onBlur: handleBlur,
    onChange: handleChange,
    name: props.name,
    error,
  } as const;

  const component = type === 'textarea' ? <AiTextarea {...commonProps} /> : <AiInput {...commonProps} />;

  return component;
});

PredicateInputBase.displayName = 'PredicateInputBase';

export const PredicateInput = createFormField(PredicateInputBase);
